"use client"
import { ActivityType } from "@opensea/graphql"
import { useCookie } from "@opensea/ui-kit/hooks"
import { toLowerCase, toUpperCase } from "@opensea/utils"
import { parseAsArrayOf, parseAsStringLiteral, useQueryState } from "nuqs"
import { useFilterDefaults } from "@/components/layout/FilterDefaultsProvider"
import { LONG_LASTING_COOKIE_EXPIRATION } from "@/lib/cookie"
import { QUERY_PARAM_KEYS } from "./keys"

const ACTIVITY_TYPES = [
  "all",
  ...ActivityType.map(v => toLowerCase(v)),
] as const

export type ActivityTypeQueryParam = (typeof ACTIVITY_TYPES)[number]

const ACTIVITY_TYPE_COOKIE = "activityTypes"

const parser = parseAsStringLiteral(ACTIVITY_TYPES)

export function useActivityTypeQueryParam() {
  const { activityTypes: defaultActivityTypes } = useFilterDefaults()
  const [, setActivityTypeCookie] = useCookie(ACTIVITY_TYPE_COOKIE)

  const [values, setValues] = useQueryState(
    QUERY_PARAM_KEYS.activityTypes,
    parseAsArrayOf(parser).withDefault(defaultActivityTypes ?? []),
  )

  const activityTypes = values.map(v => toUpperCase(v))

  async function onSelectActivityType(status: (typeof ACTIVITY_TYPES)[number]) {
    if (status === "all" && !activityTypes.includes("ALL")) {
      await setValues(["all"])
      setActivityTypeCookie(JSON.stringify([]), {
        expires: LONG_LASTING_COOKIE_EXPIRATION,
      })
      return
    }

    const newStatuses = (
      values.includes(status)
        ? values.filter(s => s !== status)
        : [...values, status]
    )
      // Filter out "all" if there are other statuses
      .filter(s => s !== "all")

    await setValues(newStatuses.length > 0 ? newStatuses : ["all"])
    setActivityTypeCookie(
      JSON.stringify(
        newStatuses.length > 0 ? newStatuses.map(toUpperCase) : [],
      ),
      {
        expires: LONG_LASTING_COOKIE_EXPIRATION,
      },
    )
  }

  const returnedActivityTypes = activityTypes.flatMap(type =>
    type === "ALL" ? [] : [type],
  )

  return {
    activityTypes: returnedActivityTypes,
    onSelectActivityType,
  }
}
